import React, { useState, useEffect, useRef } from "react";
import Link from "../../components/Link";
import Media from "../../components/common/Media";
import { styled } from "../../stitches.config";
import PlayerIconBackground from "../assets/icons/player-icon.svg";
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

interface IProps {
  article: any;
  media?: any;
  style?: string;
  withBackground?: boolean;
  dataAnalyticsName?: string;
  fetchpriority?: string;
}

const PhotoOnTopDiscover: React.FC<IProps> = ({
  media,
  article,
  style,
  withBackground,
  dataAnalyticsName,
  fetchpriority,
  ...props
}) => {
  const imgUrl = article?.picture?.img;
  const [height, setHeight] = useState(200);
  const pictureContainer = useRef<HTMLDivElement>(null);
  const showSkeleton = article.url ? false : true;

  useEffect(() => {
    if (pictureContainer?.current?.clientWidth) {
      const eltWidth = pictureContainer?.current?.clientWidth;
      const cHeight = Math.ceil((eltWidth * 2) / 3);

      if (cHeight !== height && cHeight > 100) {
        setHeight(cHeight);
      }
    }
  }, []);

  console.log("article:", article)

  return (

    <Wrapper
      className={
        "photo-on-top style--" +
        style +
        (withBackground ? " style--with-padding" : "")
      }
    >
      <Link to={article.settings?.link || article.url} data-analytics-name={dataAnalyticsName}>
        <StyledPhotoOnTop ref={pictureContainer} size={article.size}>
          <StyledImage>
            <Media
              media={media}
              embed={article?.embed?.url}
              src={imgUrl}
              preserveRatio={false}
              aspectRatio={1.5}
              height={height}
              fetchpriority={fetchpriority}
            />
            {article?.player && <PlayerIcon boxSize="xxl" />}
          </StyledImage>
        </StyledPhotoOnTop>
        <StyledWrap size={article?.size}>

          <span className="title">
            {showSkeleton ? <Skeleton count={3} /> : article?.settings?.title_normal || article?.pile_title || article?.title}
          </span>

          <span className="category">
            {article?.category && article.category?.name}
            <Dot>&#8226;</Dot>
            {article?.date}
          </span>

        </StyledWrap>
      </Link>

    </Wrapper>
  );
};

const StyledPhotoOnTop = styled("div", {
  variants: {
    size: {
      lg: {
        "@bp4": {
          width: "455px",
          height: "330px",
        },
        "@bp5": {
          width: "624px",
          height: "416px",
        },
      },
      md: {
        "@bp4": {
          width: "200px",
        },
        "@bp5": {
          width: "408px",
          height: "272px",
        },
      },
    },
  },
  "& a": {
    textDecoration: "none !important",
  },
});

const StyledImage = styled("div", {
  position: "relative",

  "& div": {
    borderRadius: "20px",
  },

  "& img": {
    width: "100%",
    height: "auto",
    display: "block",
  },
});

const StyledWrap = styled("div", {
  display: "block",
  color: "$grayscale100",
  padding: "8px 0 0px",
  margin: "0 auto",
  "& .title": {
    fontFamily: "$grotesk",
    fontSize: 18,
    lineHeight: "22px",
    letterSpacing: "0.2px",
    fontWeight: "700",
  },
  "& .category": {
    display: "block",
    fontSize: 12,
    lineHeight: "14px",
    marginTop: "16px",
    color: "$grayscale75",
  },
  "@bp4": {
    height: "111px",
    overflow: "hidden",

    "& .title": {
      fontSize: 18,
      lineHeight: "24px",
    },
  },
  "@bp5": {
    margin: "0",
    height: "87px",
    overflow: "hidden",
  },
  variants: {
    size: {
      lg: {
        "@bp5": {
          width: "570px",
          margin: "0",
        },
        p: {
          fontSize: "24px",
          lineHeight: "30px",
          "@bp4": {
            fontSize: "30px",
            lineHeight: "34px",
          },
          "@bp5": {
            fontSize: "38px",
            lineHeight: "42px",
          },
        },
      },
      md: {
        margin: "0",
        p: {
          fontSize: "24px",
          lineHeight: "30px",
        },
      },
    },
  },
});


const Wrapper = styled("div", {
  paddingBottom: "16px",
  width: "100%",
  "& a": {
    "&:hover": {
      textDecoration: "underline",
      textDecorationColor: "$grayscale100 !important",
    },
  },

  "&.style--red": {
    [`${StyledWrap} > span`]: {
      backgroundColor: "#EA3A3E",
      color: "white",
      boxShadow: "2px 0 0 red, -2px 0 0 #EA3A3E",
    },

    "& a": {
      "&:hover": {
        textDecorationColor: "$grayscale0Fixed !important",
      },
    },
  },

  "&.style--primary": {
    [`${StyledWrap} > span`]: {
      backgroundColor: "$primary",
      color: "white",
      boxShadow: "2px 0 0 red, -2px 0 0 $primary",
    },
    "& a": {
      "&:hover": {
        textDecorationColor: "$grayscale0Fixed !important",
      },
    },
  },

  "&.style--black": {
    [`${StyledWrap} > span`]: {
      backgroundColor: "#131212",
      color: "white",
      boxShadow: "2px 0 0 #131212, -2px 0 0 #131212",
    },
    "& a": {
      "&:hover": {
        textDecorationColor: "$grayscale0Fixed !important",
      },
    },
  },

  "&.style--with-padding": {
    backgroundColor: "$grayscale0",
    paddingBottom: "1px",
    [`${StyledWrap}`]: {
      margin: "16px",
      padding: 0,
      "@bp4": {
        height: "70px",
        overflow: "hidden"
      },
    },
  },
});

const Dot = styled('span', {
  margin: "0 4px",
});

const PlayerIcon = styled("div", {
  position: "absolute",
  zIndex: 100,
  backgroundImage: `url(${PlayerIconBackground})`,
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",

  top: "50%",
  left: "50%",
  width: "48px",
  height: "48px",
  marginLeft: "-24px",
  marginTop: "-24px",

  variants: {
    boxSize: {
      xxl: {
        backgroundSize: "48px",
      },
      xs: {
        backgroundSize: "32px",
      },
      xxs: {
        backgroundSize: "28px",
      },
    },
  },
});

export default PhotoOnTopDiscover;
